
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Form } from "@/components/ui/form";
import { BasicOptionFields } from "@/components/variant-options/form-fields/BasicOptionFields";
import { PriceChangeField } from "@/components/variant-options/form-fields/PriceChangeField";
import { DefaultOptionField } from "@/components/variant-options/form-fields/DefaultOptionField";
import { AssociationFields } from "@/components/variant-options/form-fields/AssociationFields";
import { FormActions } from "@/components/variant-options/form-fields/FormActions";
import { useFactories } from "@/hooks/useFactories";
import { useVariants } from "@/hooks/useVariants";

const formSchema = z.object({
  option_name: z.string().min(1, "Option name is required"),
  option_value: z.string().min(1, "Option value is required"),
  is_default: z.boolean().default(false),
  default_selected_option: z.boolean().default(false),
  variant: z.array(z.string()),
  factories: z.array(z.string()),
  price_change: z.coerce.number().default(0),
  image_url: z.string().optional(),
  variant_group: z.record(z.string(), z.number()).default({}),
});

type VariantOptionFormValues = z.infer<typeof formSchema>;

interface VariantOptionFormProps {
  initialData?: any;
  onSubmit: (values: VariantOptionFormValues) => Promise<boolean>;
  onCancel: () => void;
}

export function VariantOptionForm({ initialData, onSubmit, onCancel }: VariantOptionFormProps) {
  const { factories, loading: loadingFactories } = useFactories();
  const { variants, loading: loadingVariants } = useVariants();
  
  const form = useForm<VariantOptionFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      option_name: initialData?.option_name || "",
      option_value: initialData?.option_value || "",
      is_default: initialData?.is_default || false,
      default_selected_option: initialData?.default_selected_option || false,
      variant: initialData?.variant || [],
      factories: initialData?.factories || [],
      price_change: initialData?.price_change || 0,
      image_url: initialData?.image_url || "",
      variant_group: initialData?.variant_group || {},
    },
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      form.reset({
        option_name: initialData.option_name,
        option_value: initialData.option_value,
        is_default: initialData.is_default || false,
        default_selected_option: initialData.default_selected_option || false,
        variant: initialData.variant || [],
        factories: initialData.factories || [],
        price_change: initialData.price_change || 0,
        image_url: initialData.image_url || "",
        variant_group: initialData.variant_group || {},
      });
    }
  }, [initialData, form]);
  
  const handleSubmit = async (values: VariantOptionFormValues) => {
    setIsSubmitting(true);
    try {
      const result = await onSubmit(values);
      if (result) {
        form.reset();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <BasicOptionFields form={form} />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* <PriceChangeField form={form} /> */}
          <DefaultOptionField form={form} />
        </div>
        
        <AssociationFields 
          form={form}
          variants={variants}
          factories={factories}
          loadingVariants={loadingVariants}
          loadingFactories={loadingFactories}
        />
        
        <FormActions 
          onCancel={onCancel}
          isSubmitting={isSubmitting}
          isEditing={!!initialData}
        />
      </form>
    </Form>
  );
}
